import classNames from 'classnames';
import React, { useCallback, useRef, JSX } from 'react';
import { ReactComponent as CheckboxIcon } from '@/assets/avatar_base.svg';
import { cleanBoxProps } from '../../../utils/cleanBoxProps';
import { IBoxCheckboxComponent, IBoxComponent } from '@enuan/chat__utils';
import { styled } from '@mui/material/styles';
import { fontSecondaryOblique } from 'src/theme/mixins';

export type ICheckboxProps = Omit<
	IBoxComponent<IBoxCheckboxComponent<JSX.IntrinsicElements['input']>>,
	'type'
> & {
	onChange?: (value: boolean) => void;
};

export const Checkbox = styled(
	({
		checked: _checked,
		onChange,
		label,
		disabled,
		className,
		error,
		value: _value,
		children,
		..._props
	}: React.PropsWithChildren<ICheckboxProps>) => {
		const props = cleanBoxProps(_props);

		const checked = _checked ?? _value?.toString() === 'true';

		const _onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
			({ currentTarget: { checked } }) => {
				onChange?.(checked);
			},
			[onChange],
		);

		const ref = useRef<HTMLInputElement>(null);

		return (
			<div
				className={classNames(
					'checkbox',
					checked && 'checkbox--checked',
					error && 'checkbox--error',
					disabled && 'checkbox--disabled',
					className,
				)}
			>
				<input
					className="input input--checkbox"
					type="checkbox"
					disabled={disabled}
					checked={checked}
					onChange={_onChange}
					ref={ref}
					{...props}
				/>
				<CheckboxIcon
					className="icon"
					onClick={(e) => {
						e.preventDefault();
						ref.current?.click();
					}}
				/>
				{label && (
					<label
						className="label"
						onClick={(e) => {
							e.preventDefault();
							ref.current?.click();
						}}
					>
						{label}
					</label>
				)}
				{children}
			</div>
		);
	},
	{
		target: 'Checkbox-root',
		label: 'Checkbox',
	},
)({
	display: 'flex',
	position: 'relative',
	alignItems: 'center',
	color: 'var(--form__checkbox__label_color)',

	'&:not(:first-child):not(:last-child)': {
		margin: '0.5em 0',
	},

	'& label': {
		margin: 0,
		...fontSecondaryOblique,
		fontSize: '1.5em',
		cursor: 'pointer',
	},
	'& input': {
		position: 'absolute',
		left: '-9999px',
		opacity: 0,
	},
	'& .icon': {
		width: '1.3em',
		height: '1.3em',
		cursor: 'pointer',
		marginRight: '0.5em',
		display: 'inline-block',
		fill: 'var(--form__checkbox__icon_fill)',
		stroke: 'var(--form__checkbox__icon_stroke)',
		strokeWidth: 2,
		transform: 'scaleX(-1)',
	},

	'&.checkbox--disabled': {
		cursor: 'default',
		color: 'var(--form__checkbox--disabled_color)',
	},
	'&.checkbox--checked': {
		color: 'var(--form__checkbox--checked__label_color)',
		'& .icon': {
			fill: 'var(--form__checkbox--checked__icon_fill)',
			stroke: 'var(--form__checkbox--checked__icon_stroke)',
		},
	},
	'&.checkbox--error': {
		color: 'var(--form__checkbox--error_color)',
	},
});
